import { getPersistentJson, setPersistentItem } from '../vrs-auth/storage';
import { isCertifiedIpCtsEnabled } from '../base/whitelabel/certifiedMode';
import { clampCaptionSizePt, nearestCaptionSize, savedCaptionSizePt } from './captionSize';
import { CAPTION_TONE_SURFACE_POLICIES } from './captionToneDictionary';
import logger from './logger';
export const CAPTION_DWELL_OPTIONS = [
    { label: 'Short (2s)', value: 2000 },
    { label: 'Standard (3s)', value: 3000 },
    { label: 'Long (5s)', value: 5000 },
    { label: 'Very long (8s)', value: 8000 }
];
export const DEFAULT_CAPTION_DWELL_MS = 3000;
export const CAPTION_PREFERENCES_STORAGE_KEY = 'vrs_caption_preferences';
export const CAPTION_EXPRESSIVE_MODE_OPTIONS = [
    { label: 'Minimal', value: 'minimal' },
    { label: 'Professional', value: 'professional' },
    { label: 'Elegant', value: 'elegant' },
    { label: 'Comic', value: 'comic' },
    { label: 'Meme', value: 'meme' }
];
export const CAPTION_SIZE_OPTIONS = [
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
    { label: 'Extra Large', value: 'extraLarge' }
];
export const DEFAULT_CAPTION_PREFERENCES = {
    captionDwellMs: DEFAULT_CAPTION_DWELL_MS,
    captionSize: 'large',
    enabledByDefault: false,
    expressiveMode: 'professional',
    keepTranscript: true,
    screenReaderToneHints: false,
    showSpeakerNames: true,
    toneMarkerLane: true,
    toneMotion: false
};
function normalizePolicyExpressiveMode(value) {
    return value === 'playful' ? 'comic' : value;
}
/**
 * Resolves the caption surface a renderer should use. Certified IP CTS mode
 * overrides every surface with the plain, styling-free certified policy.
 */
export function getActiveCaptionSurface(surface = 'malkaVrsCc') {
    return isCertifiedIpCtsEnabled() ? 'certifiedIpCts' : surface;
}
export function getDefaultCaptionPreferencesForSurface(surface) {
    const policy = CAPTION_TONE_SURFACE_POLICIES[surface] || CAPTION_TONE_SURFACE_POLICIES.malkaVrsCc;
    return {
        ...DEFAULT_CAPTION_PREFERENCES,
        enabledByDefault: surface === 'relayCall',
        expressiveMode: normalizePolicyExpressiveMode(policy.defaultMode),
        toneMarkerLane: policy.defaultToneMarkerLane,
        toneMotion: policy.defaultToneMotion && policy.allowMotion
    };
}
const REGULATED_TRANSCRIPT_INDUSTRIES = new Set([
    'education',
    'government',
    'healthcare',
    'legal',
    'medical'
]);
function normalizeIndustryTag(value) {
    return typeof value === 'string'
        ? value.trim().toLowerCase().replace(/[\s_]+/g, '-')
        : '';
}
export function isCaptionTranscriptRetentionAllowed() {
    // Certified IP CTS mode: no local transcript retention until counsel
    // signs off on a retention policy (FCC roadmap, Sprint 1).
    if (isCertifiedIpCtsEnabled()) {
        return false;
    }
    const userInfo = getPersistentJson('vrs_user_info') || {};
    if (userInfo.transcript_retention_allowed === false
        || userInfo.transcriptRetentionAllowed === false
        || userInfo.captionTranscriptRetentionAllowed === false) {
        return false;
    }
    const industryTag = normalizeIndustryTag(userInfo.industry_tag || userInfo.industryTag);
    return !REGULATED_TRANSCRIPT_INDUSTRIES.has(industryTag);
}
function normalizeExpressiveMode(value) {
    return value === 'comic'
        || value === 'elegant'
        || value === 'meme'
        || value === 'minimal'
        || value === 'professional'
        ? value
        : DEFAULT_CAPTION_PREFERENCES.expressiveMode;
}
function normalizeCaptionSize(value) {
    // 'medium' is a real choice: with the default at 'large' it must survive normalization.
    return value === 'extraLarge' || value === 'large' || value === 'medium' || value === 'small'
        ? value
        : DEFAULT_CAPTION_PREFERENCES.captionSize;
}
function normalizeCaptionDwell(value) {
    // Static pages persist through the same storage and may hand us strings.
    const milliseconds = typeof value === 'number' ? value : Number(value);
    return CAPTION_DWELL_OPTIONS.some(option => option.value === milliseconds)
        ? milliseconds
        : DEFAULT_CAPTION_DWELL_MS;
}
export function normalizeCaptionPreferences(value) {
    const legacyEnabled = getPersistentJson('vrs_captions_enabled');
    let enabledByDefault = DEFAULT_CAPTION_PREFERENCES.enabledByDefault;
    if (typeof legacyEnabled === 'boolean') {
        enabledByDefault = legacyEnabled;
    }
    if (typeof value?.enabledByDefault === 'boolean') {
        enabledByDefault = value.enabledByDefault;
    }
    const transcriptRetentionAllowed = isCaptionTranscriptRetentionAllowed();
    const pinched = clampCaptionSizePt(value?.captionSizePt);
    const captionSizePt = pinched === undefined ? undefined : savedCaptionSizePt(pinched);
    return {
        captionDwellMs: normalizeCaptionDwell(value?.captionDwellMs),
        captionSize: captionSizePt === undefined ? normalizeCaptionSize(value?.captionSize) : nearestCaptionSize(captionSizePt),
        ...captionSizePt === undefined ? {} : { captionSizePt },
        enabledByDefault,
        expressiveMode: normalizeExpressiveMode(value?.expressiveMode),
        keepTranscript: transcriptRetentionAllowed && typeof value?.keepTranscript === 'boolean'
            ? value.keepTranscript
            : transcriptRetentionAllowed && DEFAULT_CAPTION_PREFERENCES.keepTranscript,
        screenReaderToneHints: typeof value?.screenReaderToneHints === 'boolean'
            ? value.screenReaderToneHints
            : DEFAULT_CAPTION_PREFERENCES.screenReaderToneHints,
        showSpeakerNames: typeof value?.showSpeakerNames === 'boolean'
            ? value.showSpeakerNames
            : DEFAULT_CAPTION_PREFERENCES.showSpeakerNames,
        toneMarkerLane: typeof value?.toneMarkerLane === 'boolean'
            ? value.toneMarkerLane
            : DEFAULT_CAPTION_PREFERENCES.toneMarkerLane,
        toneMotion: typeof value?.toneMotion === 'boolean'
            ? value.toneMotion
            : DEFAULT_CAPTION_PREFERENCES.toneMotion
    };
}
export function getCaptionPreferences() {
    return normalizeCaptionPreferences(getPersistentJson(CAPTION_PREFERENCES_STORAGE_KEY));
}
const preferenceListeners = new Set();
let preferenceSaveStatus = 'idle';
let preferenceSaveRevision = 0;
let cancelPreferenceSaveTimer;
const saveListeners = new Set();
export function getCaptionPreferenceSaveStatus() {
    return preferenceSaveStatus;
}
export function subscribeCaptionPreferenceSaveStatus(listener) {
    saveListeners.add(listener);
    return () => { saveListeners.delete(listener); };
}
function notifySaveStatus(status) {
    preferenceSaveStatus = status;
    for (const listener of Array.from(saveListeners)) {
        try {
            listener();
        }
        catch {
            logger.warn('Caption save observer failed.');
        }
    }
}
/** Observe writes from shared web/native settings without coupling storage to Redux. */
export function subscribeCaptionPreferences(listener) {
    preferenceListeners.add(listener);
    return () => { preferenceListeners.delete(listener); };
}
export function setCaptionPreferences(next) {
    const revision = ++preferenceSaveRevision;
    cancelPreferenceSaveTimer?.();
    notifySaveStatus('saving');
    const timer = setTimeout(() => {
        if (revision === preferenceSaveRevision)
            notifySaveStatus('unconfirmed');
    }, 10000);
    cancelPreferenceSaveTimer = () => clearTimeout(timer);
    const confirmed = setPersistentItem(CAPTION_PREFERENCES_STORAGE_KEY, JSON.stringify(normalizeCaptionPreferences(next)), { confirm: true });
    void Promise.resolve(confirmed).then(() => {
        if (revision === preferenceSaveRevision) {
            cancelPreferenceSaveTimer?.();
            notifySaveStatus('saved');
        }
    }, () => {
        if (revision === preferenceSaveRevision) {
            cancelPreferenceSaveTimer?.();
            notifySaveStatus('unconfirmed');
        }
        logger.warn('Caption preference persistence was not confirmed.');
    });
    for (const listener of Array.from(preferenceListeners)) {
        try {
            listener();
        }
        catch {
            logger.warn('Caption preference observer failed.');
        }
    }
}
/** Retry the current device choice, never the stale values of an older screen. */
export function retryCaptionPreferenceSave() {
    setCaptionPreferences(getCaptionPreferences());
}
export function updateCaptionPreferences(patch) {
    const merged = {
        ...getCaptionPreferences(),
        ...patch
    };
    // A named size (Settings, the web) replaces a pinched size.
    if ('captionSize' in patch && !('captionSizePt' in patch)) {
        delete merged.captionSizePt;
    }
    const next = normalizeCaptionPreferences(merged);
    setCaptionPreferences(next);
    return next;
}
export function getCaptionModeClassName(mode) {
    return `caption-mode--${normalizeExpressiveMode(mode)}`;
}
export function getCaptionSizeClassName(size) {
    const normalized = normalizeCaptionSize(size);
    // Preference keys are camelCase; the matching CSS modifiers are kebab-cased.
    return `caption-size--${normalized.replace(/[A-Z]/g, char => `-${char.toLowerCase()}`)}`;
}
export function getCaptionMotionClassName(enabled) {
    return enabled ? 'caption-motion--on' : 'caption-motion--off';
}
